import { X, Calendar, Coffee, Clock, HelpCircle } from 'lucide-react';

/**
 * Inline help panel — walks a new PM / staff member through the Roster
 * workflow. Kept close to the source so any UX change here is also
 * documented in the modal.
 */
export default function HowToUseRosterModal({ isManager, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
           data-testid="how-to-use-roster-modal"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg" style={{ background: 'rgba(124,58,237,0.10)' }}>
              <HelpCircle className="w-5 h-5" style={{ color: '#7C3AED' }} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: '#0d2644' }}>How to use Roster</h2>
          </div>
          <button onClick={onClose} data-testid="how-to-close" className="p-1 rounded hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 text-sm leading-relaxed">
          <p className="text-muted-foreground">
            Roster has three tabs. Each has its own workflow — the quickest way to learn is to try the three key actions below.
          </p>

          {/* Roster tab */}
          <section>
            <h3 className="font-semibold mb-1.5 flex items-center gap-2" style={{ color: '#0d2644' }}>
              <Calendar className="w-4 h-4" /> Roster tab
            </h3>
            <ol className="list-decimal ml-5 space-y-1 text-[13.5px]">
              {isManager ? (
                <>
                  <li>Use <strong>← / →</strong> to move between weeks, or click <em>Today</em> to jump back.</li>
                  <li>Click an <strong>empty day cell</strong> next to a staff member to add a shift (start, end, break, room/site).</li>
                  <li>Shifts start as <span className="inline-block w-2 h-2 rounded-full bg-slate-400 align-middle" /> <em>Draft</em>. Click a shift, then <strong>Publish</strong> to email the staff member.</li>
                  <li>Once published, the pill turns <span className="inline-block w-2 h-2 rounded-full bg-amber-500 align-middle" /> <em>Awaiting accept</em> — becomes <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 align-middle" /> <em>Accepted</em> or <span className="inline-block w-2 h-2 rounded-full bg-rose-500 align-middle" /> <em>Declined</em> when the staff member clicks the email link.</li>
                  <li><strong>Drag</strong> a shift across days in the same row to reschedule (published shifts email the new date automatically).</li>
                  <li><em>Sites</em> button — add rooms/sites you assign shifts to (e.g. "US room 1", "CT bay").</li>
                  <li><em>Notify staff</em> — sends everyone this week's roster in one email (rather than one-per-shift).</li>
                </>
              ) : (
                <>
                  <li>You'll see your published shifts for the current week. Use <strong>← / →</strong> to see past or future weeks.</li>
                  <li>When your manager publishes a new shift, you'll get an <strong>email with Accept / Decline links</strong>. Click one to confirm.</li>
                  <li>Once you've accepted a shift, it turns green here.</li>
                  <li>To subscribe to your shifts in Apple / Google / Outlook, click <em>Send to my calendar</em> at the top right.</li>
                </>
              )}
            </ol>
          </section>

          {/* Leave */}
          <section>
            <h3 className="font-semibold mb-1.5 flex items-center gap-2" style={{ color: '#0d2644' }}>
              <Coffee className="w-4 h-4" /> Leave tab
            </h3>
            <ol className="list-decimal ml-5 space-y-1 text-[13.5px]">
              <li>{isManager ? 'Anyone can submit a leave request.' : 'Click New request, pick your dates and type (annual, personal, unpaid, other).'}</li>
              {isManager && <li>You'll see all pending requests. <strong>Approve</strong> or <strong>Decline</strong> with an optional reason.</li>}
              <li>{isManager ? 'Approved leave shows on the Roster grid as a coloured background so you know not to schedule that person.' : 'Approved leave shows on your roster automatically. You still need to record any changes with the practice manager if plans change.'}</li>
            </ol>
          </section>

          {/* Timesheets */}
          <section>
            <h3 className="font-semibold mb-1.5 flex items-center gap-2" style={{ color: '#0d2644' }}>
              <Clock className="w-4 h-4" /> Timesheets tab
            </h3>
            <ol className="list-decimal ml-5 space-y-1 text-[13.5px]">
              <li>Timesheets auto-generate from your published + accepted shifts, one per pay cycle.</li>
              <li>Adjust actual <strong>start / end / break</strong> if reality differed from the roster, then click <strong>Submit</strong>.</li>
              {isManager && <li>You review, <strong>Approve</strong> or <strong>Reject</strong>, and can export a fortnight as a <strong>Xero CSV</strong> for payroll.</li>}
            </ol>
          </section>

          {/* Sharing */}
          <section>
            <h3 className="font-semibold mb-1.5" style={{ color: '#0d2644' }}>Sharing the roster</h3>
            <ul className="list-disc ml-5 space-y-1 text-[13.5px]">
              <li><strong>Send to my calendar</strong> — a personal iCal link. Add it to your phone/Outlook and shifts update automatically.</li>
              {isManager && <li><strong>Public schedule</strong> — generates a read-only URL you can share with referrers so they know who's on when.</li>}
            </ul>
          </section>

          <div className="rounded-lg border border-purple-200 bg-purple-50 p-3 text-[13px]">
            <strong style={{ color: '#7C3AED' }}>Coming next:</strong> a <em>base weekly template</em> (define contracted hours once, generate every week with one click) and a <em>room-first view</em> (see which rooms are unstaffed at a glance) — Kronos-style workflow. Tell your practice manager if you'd like this prioritised.
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t p-3 flex justify-end">
          <button onClick={onClose} data-testid="how-to-done"
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white"
            style={{ background: '#7C3AED' }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
